import { Link, useParams } from "react-router-dom";
import Seo, { SITE_URL } from "@/components/feature/Seo";
import ComicCard from "@/components/feature/ComicCard";
import { comics } from "@/mocks/comics";

export default function SeriesDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);
  const related = comic ? comics.filter((c) => c.genre === comic.genre && c.id !== comic.id).slice(0, 4) : [];

  if (!comic) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center px-4 py-24 text-center md:px-6">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
        <h1 className="mt-4 font-heading text-2xl text-foreground-950">未找到该连载漫画</h1>
        <p className="mt-2 text-sm text-foreground-600">你访问的作品可能不存在或已被调整。</p>
        <Link
          to="/series"
          className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
        >
          返回全部连载
        </Link>
      </div>
    );
  }

  const url = `${SITE_URL}/series/${comic.id}`;

  return (
    <>
      <Seo
        title={`${comic.title} - 长期连载漫画 | 天天漫画网`}
        description={`${comic.title}：${comic.description}，天天漫画网长期连载，稳定更新不断更，追更神器在手。`}
        keywords={`${comic.title},${comic.genre}漫画,长篇漫画,长期连载,追更`}
        canonical={url}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            name: comic.title,
            url,
            description: comic.description,
            image: comic.image,
            genre: comic.genre,
            author: { "@type": "Organization", name: comic.author },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL },
              { "@type": "ListItem", position: 2, name: "全部连载", item: `${SITE_URL}/series` },
              { "@type": "ListItem", position: 3, name: comic.title, item: url },
            ],
          },
        ]}
      />

      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <a href="/" className="hover:text-primary-600">首页</a>
          <i className="ri-arrow-right-s-line"></i>
          <a href="/series" className="hover:text-primary-600">全部连载</a>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700">{comic.title}</span>
        </nav>

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr]">
          <div>
            <div className="overflow-hidden rounded-lg border border-background-200/70">
              <img src={comic.image} alt={`${comic.title} 漫画封面大图`} className="w-full object-cover" />
            </div>
            <div className="mt-5 flex flex-col gap-2.5">
              <Link
                to="/series"
                className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-center text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-add-circle-line mr-1"></i>加入追更
              </Link>
              <div className="grid grid-cols-2 gap-2.5">
                <div className="rounded-md bg-background-100 px-4 py-3 text-center">
                  <p className="text-xs text-foreground-500">已更</p>
                  <p className="mt-0.5 text-base font-bold text-foreground-950">{comic.chapters} 话</p>
                </div>
                <div className="rounded-md bg-background-100 px-4 py-3 text-center">
                  <p className="text-xs text-foreground-500">评分</p>
                  <p className="mt-0.5 flex items-center justify-center gap-1 text-base font-bold text-accent-600">
                    <i className="ri-star-fill"></i>{comic.rating}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <article>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700">{comic.genre}</span>
              <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-700">{comic.status}</span>
              <span className="rounded-md bg-background-100 px-2.5 py-1 text-xs font-medium text-foreground-600">{comic.year} 年开载</span>
            </div>
            <h1 className="mt-4 font-heading text-2xl text-foreground-950 md:text-3xl">{comic.title}</h1>
            <p className="mt-2 text-sm text-foreground-600">
              作者 <strong className="text-foreground-900">{comic.author}</strong> · 最近更新：{comic.updated}
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              {comic.tags.map((t) => (
                <span key={t} className="whitespace-nowrap rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-700">
                  #{t}
                </span>
              ))}
            </div>

            <h2 className="mt-7 text-base font-bold text-foreground-950">作品简介</h2>
            <p className="mt-2.5 leading-relaxed text-foreground-700">{comic.description}</p>

            <div className="mt-6 rounded-lg border border-background-200/70 bg-background-100 p-5">
              <h2 className="text-base font-bold text-foreground-950">更新说明</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                《{comic.title}》为天天漫画网精选的长期连载作品，保持稳定更新节奏，无特殊情况下持续更新不断更。
                加入追更后，每次新话上线都会收到提醒，让你轻松掌握最新章节动态。
              </p>
            </div>
          </article>
        </div>

        {related.length > 0 && (
          <section className="mt-16">
            <div className="flex items-end justify-between gap-4">
              <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">同类连载推荐</h2>
              <a href="/series" className="flex items-center gap-1 whitespace-nowrap text-sm text-primary-600 hover:text-primary-700">
                更多 <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
              {related.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}